/**
 * Where to send someone after they finish signing up or signing in.
 *
 * The problem this replaces: a CTA on a page with context — "Invitar a cotizar"
 * on a FixyBee's profile, "Cotizar este trabajo" on an open job — sent an
 * anonymous visitor to `/signup` and the context died there. Five different
 * URLs carried an `intent=` parameter that looked like it was solving this, and
 * **not one component in this app has ever read one** (`intent=post-job`,
 * `intent=master-trial`, `intent=dany-voice`, `promo=`, `trial=master`). They
 * were a mechanism in appearance only, which is worse than none: the request
 * looked like it had been handled.
 *
 * One mechanism instead, and it is a destination rather than an intention —
 * nothing has to be interpreted at the far end, and every hop of the auth flow
 * only has to pass it along:
 *
 *     /pro/joe  --"Invitar a cotizar"-->  /signup/client?returnTo=/jobs/new?for=joe
 *               --signup-->  /jobs/new?for=joe  -->  /client/jobs/new?for=joe
 *
 * **Only internal paths.** `safePath` is the router-side twin of the rule
 * `routes/admin.py::_validated_content` applies to a broadcast's `target_path`:
 * one leading slash, never two, no backslash, 200 characters. An open redirect
 * is the whole class of bug a `returnTo` parameter invites, and the answer to
 * an unsafe one here is silence — the caller falls back to its own default and
 * the visitor lands somewhere real. The `{role}` token the broadcast rule
 * allows is deliberately absent: it exists because a push has no router to work
 * out who is reading, and this runs inside one.
 *
 * Router-side is the only side there is: `returnTo` never reaches the backend.
 * It is read by the component that navigates, and by nothing else.
 */

/** Same cap as `MAX_TARGET_PATH` in routes/admin.py. */
export const MAX_RETURN_TO = 200;

// `_TARGET_PATH` from routes/admin.py, minus `{}`: starts with exactly one
// slash, and nothing after it that could turn into a host or a scheme.
const INTERNAL_PATH = /^\/(?!\/)[A-Za-z0-9\-._~%!$&'()*+,;=:@/?#[\]]*$/;

/**
 * `value` if it is a path this app can navigate to, otherwise `""`.
 *
 * `""` is a real answer and every caller treats it as "no destination given",
 * so a hostile value and an absent one are indistinguishable downstream. That
 * is intentional: there is nothing useful to do with a rejected redirect except
 * the thing you would have done without it.
 */
export function safePath(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw || raw.length > MAX_RETURN_TO) return "";
  // `\` is not a path separator on the web, but browsers normalize it to one,
  // so `/\evil.com` reaches a different origin than it reads as.
  if (raw.includes("\\")) return "";
  return INTERNAL_PATH.test(raw) ? raw : "";
}

/** `base` with `?returnTo=…` appended, or `base` unchanged if it is unsafe. */
export function withReturnTo(base, returnTo) {
  const safe = safePath(returnTo);
  if (!safe) return base;
  return `${base}${base.includes("?") ? "&" : "?"}returnTo=${encodeURIComponent(safe)}`;
}

/**
 * The `returnTo` of a URL, already validated.
 *
 * Takes `URLSearchParams` (what `useSearchParams` gives) or a raw `?a=b`
 * string (what `useLocation().search` gives), because both spellings are in
 * this codebase and neither is worth converting at every call site.
 */
export function readReturnTo(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  return safePath(params?.get?.("returnTo"));
}

/** The page someone is on right now, as a `returnTo` value. */
export function pathOf(location) {
  return safePath(`${location?.pathname || ""}${location?.search || ""}`);
}
